/*
  The set grid GameView shows in "card" display mode (§7.2), off MUI and off Emotion.

  It is presentational. It used to sort the array in its own render, through the old
  set-view comparator on 'cardsGraded' — which both mutated the array its parent passed in
  and applied a second, different ordering from the table's. The rows arrive already
  filtered and sorted by GameView's selectTableRows, so the grid and the table show the
  same sets in the same order.

  It renders the three states through the same resolveTableStatus the tables use, so
  switching display mode does not switch between "there is an error" and a blank page.

  **The tile is admin's catalogue tile**, not the old centred stack: logo on white with
  language and year as chips over it, then the name, then the count on its own row. The
  reasoning for each of those three lives on the TILE constants in components/styles.ts,
  because the home page's game tiles are built the same way and the rules have to be one
  thing rather than two.

  Two places this deliberately departs from admin, both because this page is public:

    - **The name wraps to two lines instead of truncating.** Admin can cut a set name off
      at one line because an editor already knows their catalogue; a visitor reading
      "Scarlet & Violet—Paldean Fates Elite Traine…" cannot tell which set that is. It is
      clamped at two and carries the full name as a title.
    - **The count keeps its label.** Admin's bare "46" sits beside a set symbol and an
      Edit button, in a screen whose whole subject is obvious. Here "46 graded" is what
      makes the number mean anything.
*/

import { Link } from "react-router-dom";

import { sanitizeURIPathSegment } from "helpers/routing";
import {
  DataTableEmpty,
  DataTableError,
  resolveTableStatus,
} from "components/DataTable";
import {
  CARD_TITLE_LINK,
  TILE,
  TILE_CHIP,
  TILE_COUNT,
  TILE_COUNT_FIGURE,
  TILE_COUNT_LABEL,
  TILE_LOGO_IMG,
  TILE_LOGO_PANEL,
  LAZY_IMG,
} from "components/styles";
import { FEATURED_LIMIT, formatCount } from "lib";

import { Set } from "types/gameInfo";

export interface GameSetsProps {
  /* Already filtered and sorted by the view. */
  data: readonly Set[];
  gameCode: string;
  isLoading?: boolean;
  /* RTK Query's error, straight through — same contract as DataTable's. */
  error?: unknown;
  /*
    The language chips. Off in the English, Japanese and Chinese buckets, where the toggle
    already says the language, and on in Other, which mixes them (pop-language-split D12).
  */
  showLanguage?: boolean;
  /*
    Load every logo straight away. Only for the short Top 10 shelf near the top of the
    page; a full grid leaves its logos to load as they scroll into view.
  */
  eager?: boolean;
}

/* Five wide at lg, like the home grid, so FEATURED_LIMIT (10) is two full rows. */
const GRID = "grid list-none grid-cols-2 gap-4 p-0 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5";

const SKELETON_TILES = FEATURED_LIMIT;

/*
  The catalogue writes releaseDate as a bare year ("2025") on most rows and as a full date
  on some. The chip has room for four characters either way, and a row with no year in it
  at all simply gets no chip rather than an empty pill.
*/
const yearOf = (releaseDate: string | null | undefined): string | null => {
  const match = /\d{4}/.exec(releaseDate || "");
  return match ? match[0] : null;
};

/*
  D30 combines same-named rows across languages, so `languages` is the real list and
  `language` is the single-row fallback. Two chips is the ceiling: a set carrying five
  languages would cover the artwork it is sitting on, and the full list is on the set's
  own page.
*/
const CHIP_LIMIT = 2;

const languagesOf = (set: Set): string[] => {
  if (set.languages && set.languages.length > 0) {
    return set.languages;
  }
  return set.language ? [set.language] : [];
};

const GameSets = (props: GameSetsProps) => {
  const { data, gameCode, isLoading = false, error, showLanguage = true, eager = false } = props;

  const status = resolveTableStatus(isLoading, error, data.length);

  if (status === "loading") {
    /* Tiles the shape of the real ones, so nothing shoves the page when they land. */
    return (
      <ul aria-hidden="true" className={GRID}>
        {Array.from({ length: SKELETON_TILES }).map((_unused, index) => (
          <li key={`skeleton-${index}`} className={TILE}>
            <span className="block h-[122px] w-full animate-pulse bg-pcg-raised" />
            <span className="m-3 block h-4 w-3/4 animate-pulse rounded bg-pcg-raised" />
            <span className="mx-3 mb-3 block h-3 w-1/3 animate-pulse rounded bg-pcg-raised" />
          </li>
        ))}
      </ul>
    );
  }

  if (status === "error") {
    return <DataTableError />;
  }

  if (status === "empty") {
    return (
      <DataTableEmpty
        title="No sets found"
        description="No set in this game matches this search."
      />
    );
  }

  return (
    <ul className={GRID}>
      {data.map((set) => {
        const to = `/${gameCode}/${sanitizeURIPathSegment(set.setName)}`;
        const languages = showLanguage ? languagesOf(set).slice(0, CHIP_LIMIT) : [];
        /* Shown cleaned (D10, D11); the link above keeps the raw name. */
        const name = set.displayName ?? set.setName;
        const year = yearOf(set.releaseDate);

        return (
          <li key={set.id} className={TILE}>
            {/*
              The same destination as the name below it. Out of the tab order and out of
              the accessibility tree so a screen reader and a keyboard get one link per
              set rather than two — it is still clickable with a mouse, which is the only
              thing it was ever for. tabIndex -1 first: aria-hidden on something focusable
              is its own violation.

              The chips live inside it rather than beside it because they are positioned
              against the white panel, and the panel is what establishes the containing
              block.
            */}
            <Link to={to} tabIndex={-1} aria-hidden="true" className={TILE_LOGO_PANEL}>
              {set.setImage ? (
                <img src={set.setImage} alt="" {...(eager ? {} : LAZY_IMG)} className={TILE_LOGO_IMG} />
              ) : (
                /* No artwork: hold the height so the row of tiles keeps one baseline. */
                <span className={`${TILE_LOGO_IMG} block`} />
              )}

              {languages.length > 0 ? (
                <span className="absolute left-1.5 top-1.5 flex flex-wrap gap-1">
                  {languages.map((language) => (
                    <span key={language} className={TILE_CHIP}>
                      {language}
                    </span>
                  ))}
                </span>
              ) : null}

              {year ? <span className={`absolute right-1.5 top-1.5 ${TILE_CHIP}`}>{year}</span> : null}
            </Link>

            <div className="flex flex-1 flex-col gap-2 px-3 py-2.5">
              <h3 className="skin-tile-title text-sm leading-tight">
                <Link to={to} title={name} className={`line-clamp-2 ${CARD_TITLE_LINK}`}>
                  {name}
                </Link>
              </h3>
              {/* mt-auto pins the count to the bottom, so a two-line name above it does
                  not push one tile's count out of line with its neighbours'. */}
              <p className={TILE_COUNT}>
                <span className={TILE_COUNT_FIGURE}>{formatCount(set.cardsGraded ?? 0)}</span>{" "}
                <span className={TILE_COUNT_LABEL}>graded</span>
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default GameSets;
